import React from "react";
import { Container, Box, Typography, Divider, Link } from "@mui/material";

export default function TermsOfServicePage() {
  return (
    <Container maxWidth="md" sx={{ py: 6 }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" fontWeight={800} gutterBottom>
          Terms of Service
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Last updated: February 24, 2026
        </Typography>
      </Box>

      <Section title="1. The Platform">
        <Typography variant="body2" sx={{ lineHeight: 1.7 }}>
          Welcome to ResourceNAV. These Terms of Service ("Terms") govern your access to and use of the ResourceNAV
          platform (the "Platform"). By accessing or using the Platform, you agree to these Terms. If you do not agree,
          do not use the Platform.
        </Typography>
      </Section>

      <Section title="2. Accounts & Eligibility">
        <Typography variant="body2" sx={{ lineHeight: 1.7 }}>
          You must be at least 18 years old to use the Platform. You are responsible for maintaining the
          confidentiality of your account credentials and for all activity that occurs under your account.
        </Typography>
        <Typography variant="body2" sx={{ lineHeight: 1.7, mt: 1 }}>
          You agree to provide accurate and current information when creating an account.
        </Typography>
      </Section>

      <Section title="3. Company Profiles & Claims">
        <Typography variant="body2" sx={{ lineHeight: 1.7, mb: 1 }}>
          a. Public company information
        </Typography>
        <Box component="ul" sx={{ pl: 3, lineHeight: 1.7, mb: 1 }}>
          <li>Company profiles may include publicly available data and third-party sources.</li>
          <li>ResourceNAV does not guarantee the accuracy or completeness of public or third-party information.</li>
        </Box>

        <Typography variant="body2" sx={{ lineHeight: 1.7, mb: 1 }}>
          b. Claiming a company profile
        </Typography>
        <Box component="ul" sx={{ pl: 3, lineHeight: 1.7, mb: 1 }}>
          <li>Users may request to claim a company profile by submitting verification information.</li>
          <li>ResourceNAV may approve, reject, or revoke claims at its discretion.</li>
          <li>Approved claimants may submit updates and contextual explanations for review.</li>
        </Box>

        <Typography variant="body2" sx={{ lineHeight: 1.7, mb: 1 }}>
          c. Responsibility for submissions
        </Typography>
        <Box component="ul" sx={{ pl: 3, lineHeight: 1.7 }}>
          <li>You represent that any content you submit is accurate to the best of your knowledge.</li>
          <li>You must have appropriate authorization to act on behalf of a company you claim.</li>
          <li>Submissions may be reviewed, approved, rejected, or published by ResourceNAV.</li>
        </Box>
      </Section>

      <Section title="4. User Content & License">
        <Typography variant="body2" sx={{ lineHeight: 1.7, mb: 1 }}>
          You retain ownership of content you submit.
        </Typography>
        <Typography variant="body2" sx={{ lineHeight: 1.7, mb: 1 }}>
          By submitting content to ResourceNAV, you grant ResourceNAV a non-exclusive, worldwide, royalty-free license to:
        </Typography>
        <Box component="ul" sx={{ pl: 3, lineHeight: 1.7, mb: 1 }}>
          <li>host, store, display, and distribute the content on the Platform,</li>
          <li>use the content for analytics, summaries, and insights related to the Platform.</li>
        </Box>
        <Typography variant="body2" sx={{ lineHeight: 1.7 }}>
          This license continues as long as the content remains on the Platform.
        </Typography>
      </Section>

      <Section title="5. Acceptable Use">
        <Typography variant="body2" sx={{ lineHeight: 1.7, mb: 1 }}>
          You agree not to:
        </Typography>
        <Box component="ul" sx={{ pl: 3, lineHeight: 1.7, mb: 1 }}>
          <li>Submit false, misleading, or deceptive information.</li>
          <li>Impersonate another individual or company.</li>
          <li>Upload malicious code or attempt to disrupt the Platform.</li>
          <li>Use the Platform for unlawful purposes.</li>
          <li>Scrape, resell, or redistribute Platform data without permission.</li>
        </Box>
        <Typography variant="body2" sx={{ lineHeight: 1.7 }}>
          ResourceNAV may suspend or terminate accounts that violate these rules.
        </Typography>
      </Section>

      <Section title="6. Updates, Publishing & Visibility">
        <Typography variant="body2" sx={{ lineHeight: 1.7 }}>
          Submitted updates may require review before publication. ResourceNAV may display status indicators (e.g.,
          "claimed", "verified", "approved update"). Published updates may be visible to all users of the Platform.
          ResourceNAV reserves the right to remove or modify content that violates these Terms or applicable laws.
        </Typography>
      </Section>

      <Section title="7. Analytics, Signals & Alerts">
        <Typography variant="body2" sx={{ lineHeight: 1.7 }}>
          ResourceNAV may generate system-derived insights, alerts, or signals based on platform activity. These features
          are informational only and do not constitute investment advice. Certain features may be limited to claimed or
          subscribed accounts.
        </Typography>
      </Section>

      <Section title="8. Third-Party Data Sources (Financials, Assets, Operations & Forecasts)">
        <Typography variant="body2" sx={{ lineHeight: 1.7, mb: 1 }}>
          ResourceNAV displays certain datasets from third-party providers, including fundamental financial data
          provided by TwelveData under a commercial license. We may also display assets, operations, and forecast data
          from third-party sources or from company-submitted information.
        </Typography>
        <Box component="ul" sx={{ pl: 3, lineHeight: 1.7, mb: 1 }}>
          <li>
            Data is provided "as is" and may be delayed, incomplete, or inaccurate.
          </li>
          <li>
            ResourceNAV does not guarantee the accuracy, completeness, or timeliness of third-party data.
          </li>
          <li>
            You should not rely on this data for investment decisions; it is for informational purposes only.
          </li>
        </Box>
      </Section>

      <Section title="9. No Investment Advice">
        <Typography variant="body2" sx={{ lineHeight: 1.7 }}>
          All information provided on ResourceNAV is for informational purposes only. Nothing on the Platform
          constitutes investment advice, a recommendation to buy or sell securities, or an offer or solicitation.
        </Typography>
        <Typography variant="body2" sx={{ lineHeight: 1.7, mt: 1 }}>
          You are solely responsible for your investment decisions.
        </Typography>
        <Typography variant="body2" sx={{ lineHeight: 1.7, mt: 1 }}>
          For additional disclosures, see our{" "}
          <Link
            href="https://naturalresourcestocks.net/financial-disclaimer-for-galt-consulting-group-dba-naturalresourcestocks-net/"
            underline="hover"
            target="_blank"
            rel="noopener noreferrer"
          >
            Financial Disclaimer
          </Link>
          .
        </Typography>
      </Section>

      <Section title="10. Intellectual Property">
        <Typography variant="body2" sx={{ lineHeight: 1.7 }}>
          The Platform, including software, design, trademarks, and compiled data, is owned by ResourceNAV or its
          licensors. You may not copy, modify, or distribute Platform materials without permission.
        </Typography>
      </Section>

      <Section title="11. Termination">
        <Typography variant="body2" sx={{ lineHeight: 1.7 }}>
          ResourceNAV may suspend or terminate your access for violations of these Terms, to protect platform integrity,
          or as required by law. You may stop using the Platform at any time.
        </Typography>
      </Section>

      <Section title="12. Disclaimers">
        <Typography variant="body2" sx={{ lineHeight: 1.7 }}>
          The Platform is provided "as is" and "as available". ResourceNAV disclaims all warranties, including implied
          warranties of accuracy, fitness for a particular purpose, and non-infringement.
        </Typography>
      </Section>

      <Section title="13. Limitation of Liability">
        <Typography variant="body2" sx={{ lineHeight: 1.7 }}>
          To the fullest extent permitted by law:
        </Typography>
        <Box component="ul" sx={{ pl: 3, lineHeight: 1.7, mb: 1 }}>
          <li>ResourceNAV shall not be liable for indirect, incidental, or consequential damages.</li>
          <li>
            ResourceNAV's total liability shall not exceed the amount you paid (if any) to use the Platform in the
            preceding 12 months.
          </li>
        </Box>
      </Section>

      <Section title="14. Governing Law">
        <Typography variant="body2" sx={{ lineHeight: 1.7 }}>
          These Terms are governed by the laws of the jurisdiction in which ResourceNAV is organized, without regard to
          conflict of law principles.
        </Typography>
      </Section>

      <Section title="15. Changes to These Terms">
        <Typography variant="body2" sx={{ lineHeight: 1.7 }}>
          We may update these Terms from time to time. Changes will be posted on this page with an updated effective
          date. Continued use of the Platform constitutes acceptance of the updated Terms.
        </Typography>
      </Section>

      <Section title="16. Contact Us">
        <Typography variant="body2" sx={{ lineHeight: 1.7 }}>
          For questions about these Terms, contact us at{" "}
          <Link href="mailto:ResourceNAV@naturalresourcestocks.net">ResourceNAV@naturalresourcestocks.net</Link>.
        </Typography>
      </Section>
    </Container>
  );
}

function Section({ title, children }) {
  return (
    <Box sx={{ mb: 4 }}>
      <Typography variant="h6" fontWeight={700} gutterBottom>
        {title}
      </Typography>
      {children}
      <Divider sx={{ mt: 2 }} />
    </Box>
  );
}
